import { CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { portalPaths } from '@provision/portal-routes';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_RowVirtualizer,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pathOr, stringToPath } from 'remeda';
import { DynamicIcon, MrtTopToolbar, Tooltip } from 'src/components';
import { EllipsesMenu } from 'src/components/EllipsesMenu/ellipses-menu';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { useStripeBillingPortalUrl } from 'src/graphql/entities/payment-portal-url';
import { useSubscriptionsReport } from 'src/graphql/entities/subscriptions-report/hooks/use-subscriptions-report';
import { SubscriptionsReportRow } from 'src/graphql/entities/subscriptions-report/subscriptions-report.types';
import { useGlobalStore } from 'src/store';
import { triggerCSVExport } from 'src/utils/csv';
import { useNotifications, useScrollPaginateHandler } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { STRIPE_STATUSES, STRIPE_STATUSES_MAP } from './const';
import { useSubscriptionsReportTableColumns } from './use-subscriptions-report-table-columns';
import { formatReportData } from './utils';

const SCROLL_OFFSET_PX = 300;
const PAGE_SIZE = 40;

const statusFilterOptions: SelectMenuOption[] = STRIPE_STATUSES.map((stripeStatus) => ({
  value: stripeStatus.value,
  title: stripeStatus.name,
}));

export const SubscriptionsReportTable = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const [statusFilter, setStatusFilter] = useState(STRIPE_STATUSES_MAP.active);
  const [globalFilter, setGlobalFilter] = useState('');
  const { generateStripeBillingPortalUrl } = useStripeBillingPortalUrl();
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { navigate } = useNavigateRoute();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: subscriptionsReportData,
    loading: isLoading,
    totalRecords,
    loadAll,
    reset,
  } = useSubscriptionsReport({
    partnerId: selectedPartnerId!,
    status: statusFilter === STRIPE_STATUSES_MAP.all ? '' : statusFilter,
  });

  const columns = useSubscriptionsReportTableColumns();

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!tableContainerRef.current) {
      return;
    }

    const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current;
    if (
      scrollHeight - scrollTop - clientHeight < SCROLL_OFFSET_PX &&
      !isLoading &&
      currentPage * PAGE_SIZE < totalRecords
    ) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [isLoading, totalRecords, currentPage]);

  const handleScroll = useScrollPaginateHandler({
    loadMore: () => {
      setCurrentPage((prev) => prev + 1);
      return null;
    },
    enabled: currentPage * PAGE_SIZE < totalRecords,
    thresholdPx: 100,
  });

  const filteredData = useMemo(() => {
    if (!subscriptionsReportData) return [];

    if (!globalFilter) return subscriptionsReportData;

    const searchTextLowerCase = globalFilter.toLowerCase();
    return subscriptionsReportData.filter((row) =>
      Object.values(row).some((value: string) =>
        value?.toString().toLowerCase().includes(searchTextLowerCase),
      ),
    );
  }, [subscriptionsReportData, globalFilter]);

  const visibleData = useMemo(() => {
    const endIndex = currentPage * PAGE_SIZE;
    return filteredData.slice(0, endIndex);
  }, [filteredData, currentPage]);

  const handleGoToPaymentPortal = useCallback(
    async (row: SubscriptionsReportRow) => {
      const url = await generateStripeBillingPortalUrl({
        addressId: row.addressId,
      });

      if (!url?.url) {
        return;
      }

      window.open(url.url, '_blank', 'noreferrer');
    },
    [generateStripeBillingPortalUrl],
  );

  const handleExport = useCallback(() => {
    try {
      const rows = formatReportData(subscriptionsReportData ?? []);

      const columnsWithoutIndex = columns?.filter((col) => col.accessorKey !== 'rowNumber');
      const headers = columnsWithoutIndex?.map((i) => i.header ?? '') ?? [];

      const dataRows = rows.map((c) =>
        columnsWithoutIndex?.map(
          (column) => pathOr(c, stringToPath(column.accessorKey as string), '') ?? '',
        ),
      ) as string[][];

      triggerCSVExport(
        `subscriptions_report_${statusFilter}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`,
        [headers].concat(dataRows),
      );

      showSuccessAlert('Export has been successfully completed');
    } catch (error) {
      console.error(error);
      showErrorAlert('Something went wrong during export');
    }
  }, [columns, statusFilter, showSuccessAlert, showErrorAlert, subscriptionsReportData]);

  const handleChangeStatusFilter = useCallback(
    (value: string) => {
      reset();
      setStatusFilter(value);
      setCurrentPage(1);
      setGlobalFilter('');
    },
    [reset],
  );

  const config = useMemo((): MRT_TableOptions<SubscriptionsReportRow> => {
    return {
      data: visibleData ?? [],
      columns,
      getRowId: (row) => row.subscriptionId,
      positionActionsColumn: 'last',
      enableTableHead: Boolean(visibleData.length > 0),
      enableRowActions: true,
      renderRowActions: ({ row, table }) => (
        <V2ThemeProvider>
          <EllipsesMenu
            title={table.options.localization.rowActions}
            options={[
              {
                label: 'View latest invoice',
                icon: 'receipt',
                iconLibrary: 'lucide',
                disabled: !row.original.latestInvoiceUrl,
                onClick: () =>
                  window.open(
                    row.original.latestInvoiceUrl ?? undefined,
                    '_blank',
                    'noreferrer',
                  ),
              },
              {
                label: 'Go to Payment Portal',
                icon: 'credit-card',
                iconLibrary: 'lucide',
                onClick: () => void handleGoToPaymentPortal(row.original),
              },
              {
                label: 'Go to Site',
                icon: 'home',
                iconLibrary: 'lucide',
                onClick: () => navigate(portalPaths.site.full(row.original.addressId)),
              },
            ]}
          />
        </V2ThemeProvider>
      ),
      enableRowVirtualization: true,
      enableColumnVirtualization: true,
      state: {
        isLoading: isLoading && !visibleData.length,
      },
      defaultColumn: {
        enableSorting: true,
        enableColumnActions: false,
      },
      enableBottomToolbar: false,
      enableColumnOrdering: true,
      enableColumnPinning: true,
      initialState: {
        density: 'compact',
        columnPinning: { right: ['mrt-row-actions'] },
      },
      enableGlobalFilter: true,
      muiSearchTextFieldProps: {
        placeholder: 'Filter results',
      },
      manualFiltering: true,
      onGlobalFilterChange: setGlobalFilter,
      enableColumnFilters: false,
      muiTableContainerProps: {
        ref: tableContainerRef,
        onScroll: handleScroll,
        sx: {
          maxHeight: 'calc(100% - 60px)',
          overflowY: 'auto',
        },
      },
      muiTableHeadCellProps: {
        sx: (theme) => ({
          backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLowest,
        }),
      },
      muiTablePaperProps: {
        sx: {
          boxShadow: 'none',
          height: '100%',
        },
      },
      enableStickyHeader: true,
      rowVirtualizerInstanceRef,
      rowVirtualizerOptions: { overscan: 4 },
      enableSorting: true,
      enableFullScreenToggle: false,
      enableDensityToggle: false,
      enableHiding: false,
      enablePagination: false,
      renderTopToolbarCustomActions: () => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography mr={1} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {(globalFilter ? filteredData?.length : subscriptionsReportData?.length) || 0}{' '}
            records
          </Typography>
          {isLoading ? <CircularProgress size={16} thickness={5} /> : null}
          <Typography variant="body2" color="text.secondary">
            View
          </Typography>
          <V2ThemeProvider>
            <SelectMenu
              label="View"
              options={statusFilterOptions}
              value={statusFilter}
              onChange={handleChangeStatusFilter}
              menuWidth="12rem"
            />
          </V2ThemeProvider>
          <Tooltip title="Export results">
            <IconButton disabled={isLoading || !visibleData.length} onClick={handleExport}>
              <DynamicIcon name="file_download" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      renderTopToolbar: ({ table }) => {
        return <MrtTopToolbar table={table} />;
      },
    };
  }, [
    visibleData,
    columns,
    isLoading,
    handleScroll,
    handleGoToPaymentPortal,
    statusFilter,
    handleExport,
    handleChangeStatusFilter,
    filteredData,
    subscriptionsReportData,
    globalFilter,
    navigate,
  ]);

  const table = useMaterialReactTable(config);
  return <MaterialReactTable table={table} />;
};
